/**
 * Attributes Slice
 * Redux slice for managing attributes state
 */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { fetchAttributesAPI, updateAttributesAPI } from 'src/services/api/attributesService';
import {
  AttributeDetails,
  AttributeDetailsInput,
  AttributeState,
  ManageAttributesTableItems,
} from 'src/types/attributes/attributes.types';

// Cache timeout: 5 minutes in milliseconds
const CACHE_TIMEOUT_MS = 5 * 60 * 1000;

const initialState: AttributeState = {
  masterAttributesData: [],
  selectedAttribute: null,
  isLoading: false,
  error: null,
  hasAttemptedFetch: false,
  hasSuccessfulFetch: false,
  lastFetchTimestamp: null,
};

export const fetchAttributes = createAsyncThunk(
  'attributes/fetchAttributes',
  async (forceRefresh: boolean = false, { rejectWithValue, getState }) => {
    try {
      const state = getState() as { attributes: AttributeState };

      const { hasSuccessfulFetch, lastFetchTimestamp } = state.attributes;

      if (!forceRefresh && hasSuccessfulFetch && lastFetchTimestamp) {
        const cacheAge: number = Date.now() - lastFetchTimestamp;
        if (cacheAge < CACHE_TIMEOUT_MS) {
          return state.attributes.masterAttributesData;
        }
      }
      return await fetchAttributesAPI();
    } catch (error) {
      return rejectWithValue((error as Error).message || 'Failed to fetch attributes');
    }
  }
);

export const updateAttributes = createAsyncThunk(
  'attributes/updateAttributes',
  async (attributes: AttributeDetailsInput, { rejectWithValue }) => {
    try {
      return await updateAttributesAPI(attributes);
    } catch (error) {
      return rejectWithValue((error as Error).message || 'Failed to update attributes');
    }
  }
);

const attributeSlice = createSlice({
  name: 'attributes',
  initialState,
  reducers: {
    clearError: (state: AttributeState) => {
      state.error = null;
    },
    resetFetchStatus: (state: AttributeState) => {
      state.hasAttemptedFetch = false;
      state.hasSuccessfulFetch = false;
      state.lastFetchTimestamp = null;
    },
    reset: () => initialState,
    setSelectedAttribute: (state: AttributeState, action: PayloadAction<ManageAttributesTableItems | null>) => {
      state.selectedAttribute = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder

      .addCase(fetchAttributes.pending, (state: AttributeState) => {
        state.isLoading = true;
        state.error = null;
        state.selectedAttribute = null;
      })
      .addCase(fetchAttributes.fulfilled, (state: AttributeState, action) => {
        state.isLoading = false;
        state.hasAttemptedFetch = true;
        state.hasSuccessfulFetch = true;
        state.lastFetchTimestamp = Date.now();
        state.error = null;
        state.masterAttributesData = action.payload as AttributeDetails[];
      })
      .addCase(fetchAttributes.rejected, (state: AttributeState, action) => {
        state.isLoading = false;
        state.hasAttemptedFetch = true;
        state.hasSuccessfulFetch = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, resetFetchStatus, reset, setSelectedAttribute } = attributeSlice.actions;

export default attributeSlice.reducer;
